import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {SignedLinkDirective} from 'src/app/directives/signed-link.directive';
import {
    DxDataGridComponent,
    DxDateBoxComponent,
    DxPopoverComponent,
    DxTemplateDirective
} from 'devextreme-angular';
import {
    DxiColumnComponent,
    DxiItemComponent, DxoScrollingComponent, DxoToolbarComponent
} from 'devextreme-angular/ui/nested';
import {
    ImportFileType,
    FileImportsClient,
    FileImportByHourDto,
    FileImportStatus,
    StatsLine,
    GetFileImportsByHourQuery,
    UploadManagerService
} from "@services";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DatePipe, LowerCasePipe, NgClass} from "@angular/common";
import {Nl2brPipe} from "../../../pipes/nl-2-br.pipe";
import {ImportFileTypePipe} from "../../../pipes/Import-file-type.pipe";

type DataSourceLine = { [key: string]: StatsLine; };

@Component({
    selector: 'app-file-import-by-hour',
    templateUrl: './file-import-by-hour.component.html',
    styleUrls: ['./file-import-by-hour.component.scss'],
    standalone: true,
    imports: [
        DxDataGridComponent,
        DxDateBoxComponent,
        DxPopoverComponent,
        DxTemplateDirective,
        DxiColumnComponent,
        DxiItemComponent,
        DxoToolbarComponent,
        DatePipe,
        LowerCasePipe,
        NgClass,
        DxoScrollingComponent,
        Nl2brPipe,
        ImportFileTypePipe,
        SignedLinkDirective,
    ],
    providers: [ImportFileTypePipe]
})
export class FileImportByHourComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    private fileImportsClient = inject(FileImportsClient);
    protected uploadManager = inject(UploadManagerService);

    dataSourceByHour: DataSourceLine[] = [];
    selectedDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
    selectedFileType: ImportFileType | null = null;
    selectedAllowMultiple = false;

    getCellData = (cellInfo: { data: DataSourceLine }) => cellInfo.data;

    private readonly popoverHideDelayMs = 200;

    showPopover(cell: any): void {
        if (cell.hideTimer) {
            clearTimeout(cell.hideTimer);
            cell.hideTimer = null;
        }
        cell.popupVisible = true;
    }

    scheduleHidePopover(cell: any): void {
        if (cell.hideTimer) clearTimeout(cell.hideTimer);
        cell.hideTimer = setTimeout(() => {
            cell.popupVisible = false;
            cell.hideTimer = null;
        }, this.popoverHideDelayMs);
    }

    async ngOnInit(): Promise<void> {
        await this.refresh();
    }

    private isSameHour(hour: number): boolean {
        return this.currentHour() === hour;
    }

    refresh = async () => {
        const command = new GetFileImportsByHourQuery({
            date: this.selectedDate,
            offsetMinutes: this.selectedDate.getTimezoneOffset()
        });
        this.fileImportsClient.getFileImportsByHour(command)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: FileImportByHourDto) => {
                this.dataSourceByHour = data.stats;
            });
    };

    currentHour = () => (new Date()).getHours();

    isCellSuccess(data: StatsLine | null) {
        return data?.status === FileImportStatus.Completed;
    }

    isCellError(data: StatsLine | null) {
        return data?.status === FileImportStatus.Failed;
    }

    isCellEmpty(data: StatsLine | null) {
        return data == null || data.status == null;
    }

    getHighlightedClass(hour: number) {
        return this.isSameHour(hour) ? 'hour cell-highlighted' : 'hour';
    }

    async onDateChanged(event: any) {
        this.selectedDate = new Date(event.value.setHours(0, 0, 0, 0));
        await this.refresh();
    }

    onUploadButtonClick(statsLine: StatsLine) {
        if (this.uploadManager.isTypeActive(statsLine.fileType)) return;

        this.selectedFileType = statsLine.fileType;
        this.selectedAllowMultiple = statsLine.allowMultipleFileUpload;

        const input = document.getElementById('file-input') as HTMLInputElement | null;
        if (!input) return;

        if (this.selectedAllowMultiple) {
            input.setAttribute('multiple', 'multiple');
        } else {
            input.removeAttribute('multiple');
        }
        input.click();
    }

    async uploadFile(event: Event) {
        const el = event.currentTarget as HTMLInputElement | null;
        if (!el || !el.files || el.files.length === 0 || this.selectedFileType == null) {
            if (el) el.value = '';
            return;
        }

        this.uploadManager.startUpload(this.selectedFileType, Array.from(el.files), this.selectedAllowMultiple);

        el.value = '';
        this.selectedFileType = null;
        this.selectedAllowMultiple = false;
        await this.refresh();
    }

    protected toDateTimeString(hour: any) {
        return '1970-01-01T' + (hour < 10 ? '0' + hour : hour) + ':00:00';
    }
}
